import buttonStyles from '@styles/scss/buttons/_buttons.module.scss';
import styles from '@styles/scss/performer/_performerContent.module.scss';
import genderUrl from 'lib/functions/genderFunction';
import { Performer } from 'lib/types/item/performer';
import { PerformerPage } from 'lib/types/item/performerPage';
import Link from 'next/link';
import { useEffect, useState } from 'react';

export default function PerformerContent({
    performer,
    performerPage,
    currentLang,
    gender,
}: {
    performer: Performer;
    performerPage: PerformerPage;
    currentLang: string;
    gender: string;
}): JSX.Element {
    const [width, setWidth] = useState(0);
    const [displayAboutMe, setDisplayAboutMe] = useState(true);
    const [displayStats, setDisplayStats] = useState(false);
    const [displayDetails, setDisplayDetails] = useState(false);

    const fullStar = (
        <svg
            width="20"
            height="19"
            viewBox="0 0 20 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
                d="M10 15.27L16.18 19L14.54 11.97L20 7.24L12.81 6.63L10 0L7.19 6.63L0 7.24L5.46 11.97L3.82 19L10 15.27Z"
                fill="#C9BA7C"
            />
        </svg>
    );

    const halfStar = (
        <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
                d="M20 7.24L12.81 6.62L10 0L7.19 6.63L0 7.24L5.46 11.97L3.82 19L10 15.27L16.18 19L14.55 11.97L20 7.24ZM10 13.4V4.1L11.71 8.14L16.09 8.52L12.77 11.4L13.77 15.68L10 13.4Z"
                fill="#C9BA7C"
            />
        </svg>
    );

    const emptyStar = (
        <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
                d="M10.3876 14.6279L10 14.394L9.61245 14.6279L4.9542 17.4394L6.19039 12.1404L6.29323 11.6995L5.95108 11.4031L1.83463 7.83704L7.2534 7.37732L7.70405 7.33908L7.88054 6.92267L10 1.92195L12.1195 6.92267L12.2959 7.33908L12.7466 7.37732L18.1654 7.83704L14.0489 11.4031L13.7068 11.6995L13.8096 12.1404L15.0458 17.4394L10.3876 14.6279Z"
                stroke="#C9BA7C"
                strokeWidth="1.5"
            />
        </svg>
    );

    const fiveStar = [fullStar, fullStar, fullStar, fullStar, fullStar];
    const fourHalfStar = [fullStar, fullStar, fullStar, fullStar, halfStar];
    const fourStar = [fullStar, fullStar, fullStar, fullStar, emptyStar];
    const threeHalfStar = [fullStar, fullStar, fullStar, halfStar, emptyStar];
    const threeStar = [fullStar, fullStar, fullStar, emptyStar, emptyStar];
    const twoHalfStar = [fullStar, fullStar, halfStar, emptyStar, emptyStar];
    const twoStar = [fullStar, fullStar, emptyStar, emptyStar, emptyStar];
    const oneHalfStar = [fullStar, halfStar, emptyStar, emptyStar, emptyStar];
    const oneStar = [fullStar, emptyStar, emptyStar, emptyStar, emptyStar];
    const zeroStar = [emptyStar, emptyStar, emptyStar, emptyStar, emptyStar];

    useEffect(() => {
        setWidth(window.innerWidth);
        const handleResizeWindow = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResizeWindow);
        return () => {
            window.removeEventListener('resize', handleResizeWindow);
        };
    }, []);

    const showAboutMe = () => {
        if (width > 450) {
            return;
        }
        setDisplayAboutMe(true);
        setDisplayDetails(false);
        setDisplayStats(false);
    };

    const showStats = () => {
        if (width > 450) {
            return;
        }
        setDisplayAboutMe(false);
        setDisplayDetails(false);
        setDisplayStats(true);
    };

    const showDetails = () => {
        if (width > 450) {
            return;
        }
        setDisplayAboutMe(false);
        setDisplayDetails(true);
        setDisplayStats(false);
    };

    const rating = () => {
        if (performer.rating >= 90) {
            return fiveStar;
        } else if (performer.rating >= 80) {
            return fourHalfStar;
        } else if (performer.rating >= 70) {
            return fourStar;
        } else if (performer.rating >= 60) {
            return threeHalfStar;
        } else if (performer.rating >= 50) {
            return threeStar;
        } else if (performer.rating >= 40) {
            return twoHalfStar;
        } else if (performer.rating >= 30) {
            return twoStar;
        } else if (performer.rating >= 20) {
            return oneHalfStar;
        } else if (performer.rating >= 10) {
            return oneStar;
        } else {
            return zeroStar;
        }
    };

    const checkNull = (item: string) => {
        if (item === null || item === '') {
            return 'N/A';
        } else {
            return item;
        }
    };
    const commaAndspace = performer.tags_new.length > 1 ? ', ' : '';

    return (
        <div className={styles.chatroomInfo}>
            <div className={styles.username}>
                <h2>{performerPage.about}</h2>
                <div className={styles.rating}>{rating()}</div>
            </div>
            <div className={styles.infoContainer}>
                <div className={styles.titleContainer}>
                    <p
                        className={`${styles.infoTitles} ${
                            displayAboutMe ? styles.selected : null
                        }`}
                        role="presentation"
                        onClick={() => showAboutMe()}>
                        <span>{performerPage.about_me}</span>
                    </p>
                    <p
                        className={`${styles.infoTitles} ${styles.stats} ${
                            displayStats ? styles.selected : null
                        }`}
                        role="presentation"
                        onClick={() => showStats()}>
                        <span>{performerPage.stats}</span>
                    </p>
                    <p
                        className={`${styles.infoTitles} ${styles.details} ${
                            displayDetails ? styles.selected : null
                        }`}
                        role="presentation"
                        onClick={() => showDetails()}>
                        <span>{performerPage.details}</span>
                    </p>
                </div>
                <div className={styles.contentContainer}>
                    {displayAboutMe ? (
                        <div className={`${styles.aboutMeSection} ${styles.infoSection}`}>
                            <p dangerouslySetInnerHTML={{ __html: performer.custom_bio && currentLang === 'en' ? performer.custom_bio : performer.about_me }}></p>
                            <p>
                                <strong>Tags:</strong>{' '}
                                {performer.ethnicity_new.url != null && gender != 'tranny' ? (
                                    <Link href={performer.ethnicity_new.url}>
                                        {performer.ethnicity_new.ethnicity + commaAndspace}
                                    </Link>
                                ) : (
                                    `${
                                        performer.ethnicity_new.ethnicity
                                            ? `${performer.ethnicity_new.ethnicity + commaAndspace}`
                                            : null
                                    }`
                                )}
                                {performer.tags_new.map((t: any, i: any) =>
                                    t.url != null && gender != 'tranny' ? (
                                        <Link href={t.url}>
                                            {t.tag +
                                                (i + 1 === performer.tags_new.length ? '' : ', ')}
                                        </Link>
                                    ) : (
                                        `${
                                            t.tag +
                                            (i + 1 === performer.tags_new.length ? '' : ', ')
                                        }`
                                    )
                                )}
                            </p>
                        </div>
                    ) : null}
                    {displayStats || width > 450 ? (
                        <div className={`${styles.statsContainer} ${styles.infoSection}`}>
                            <span>
                                <strong>{performerPage.gender}:</strong>{' '}
                            </span>
                            <Link href={genderUrl(gender)}>{checkNull(performer.gender)}</Link>
                            <br />
                            <span>
                                <strong>{performerPage.location}: </strong>
                            </span>
                            <span>{checkNull(performer.location)}</span>
                            <br />
                            <span>
                                <strong>{performerPage.age}: </strong>
                            </span>
                            <span>{checkNull(performer.age)}</span>
                            <br />
                            <span>
                                <strong>{performerPage.sexual_preference}: </strong>
                            </span>
                            <span>{checkNull(performer.sexual_preference)}</span>
                            <br />
                            <span>
                                <strong>{performerPage.body_type}: </strong>
                            </span>
                            <span>
                                {gender === 'boys'
                                    ? checkNull(performer.body_type_men)
                                    : checkNull(performer.body_type)}
                            </span>
                            <br />
                            <span>
                                <strong>{performerPage.eye_color}: </strong>
                            </span>
                            <span>{checkNull(performer.eye_color)}</span>
                            <br />
                            <span>
                                <strong>{performerPage.hair}: </strong>
                            </span>
                            <span>{checkNull(performer.hair)}</span>
                            <br />
                            {gender === 'girls' ? (
                                <>
                                    <span>
                                        <strong>{performerPage.breast_size}: </strong>
                                    </span>
                                    <span>{checkNull(performer.breast_size)}</span>
                                </>
                            ) : (
                                <>
                                    <span>
                                        <strong>{performerPage.penis_size}: </strong>
                                    </span>
                                    <span>{checkNull(performer.penis_size)}</span>
                                </>
                            )}
                        </div>
                    ) : null}
                    {displayDetails || width > 450 ? (
                        <div className={`${styles.detailsContainer} ${styles.infoSection}`}>
                            <span className={styles.detailTitles}>
                                {performerPage.i_get_excited_by}:
                            </span>
                            <p>{checkNull(performer.excited_by)}</p>
                            <span className={styles.detailTitles}>
                                {performerPage.i_dont_like}:
                            </span>
                            <p>{checkNull(performer.dont_like)}</p>
                            <span className={styles.detailTitles}>{performerPage.how_i_look}:</span>
                            <p>{checkNull(performer.look)}</p>
                        </div>
                    ) : null}
                </div>
            </div>
            <div className={styles.buttonContainer}>
                <a
                    className={`${buttonStyles.primaryButton} ${styles.button}`}
                    href={`https://rabbits.webcam/?id=2333&product_id=36&tag=signup&passthru1=${gender}&passthru2=${currentLang}&exid={exid}&tfid={tfid}`}
                    data-event-category="Outbound Click"
                    data-event-action="Join Click"
                    data-event-label="CTA button below BioText">
                    <span>{performerPage.join_now.toUpperCase()}</span>
                </a>
            </div>
        </div>
    );
}
